import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateAuthUser  } from "@/lib/api/betterAuth";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AsYouType as PhoneNumberFormatterAsYouType } from "libphonenumber-js";
import { parsePhoneNumberToE164Strict, zodPhoneNumberRefine } from "@/lib/validation/phoneNumber";

// Form validation schema - transforms phone number to E164 format
const CreateUserFormValidation = z.object({
  name: z.string().min(1, "Name is required"),
  phoneNumber: z.string()
    .min(1, "Phone number is required")
    .transform(parsePhoneNumberToE164Strict)
    .refine((value) => value !== undefined, {
      message: "Please enter a valid phone number",
      path: ["phoneNumber"],
    })
    .refine(zodPhoneNumberRefine("phoneNumber"), {
      message: "Please enter a valid phone number",
      path: ["phoneNumber"],
    }),
});

type FormData = z.infer<typeof CreateUserFormValidation>;

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
}

export function CreateUserModal({
  open,
  onClose,
  organizationId,
}: CreateUserModalProps) {
  const { toast } = useToast();
  const [phoneInput, setPhoneInput] = useState("");
  
  const createUserMutation = useCreateAuthUser();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(CreateUserFormValidation),
    defaultValues: {
      name: "",
      phoneNumber: "",
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      reset();
      setPhoneInput("");
    }
  }, [open, reset]);

  // Handle phone number input formatting (national format)
  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatter = new PhoneNumberFormatterAsYouType("US");
    const formattedValue = formatter.input(value) ?? "";
    setPhoneInput(formattedValue);
    setValue("phoneNumber", formattedValue, { shouldValidate: true });
  };

  const onSubmit = async (data: FormData) => {
    if (!organizationId) {
      toast({
        title: "Error",
        description: "Organization ID is required",
        variant: "destructive",
      });
      return;
    }

    // Validate with safeParse before submitting
    const validationResult = CreateUserFormValidation.safeParse({
      name: data.name,
      phoneNumber: data.phoneNumber,
    });

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError?.message || "Please check your input",
        variant: "destructive",
      });
      return;
    }

    // Phone number is already transformed to E164 format by zod transform
    if (!validationResult.data.phoneNumber) {
      toast({
        title: "Error",
        description: "Please enter a valid phone number",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await createUserMutation.mutateAsync({
        name: validationResult.data.name.trim(),
        phoneNumber: validationResult.data.phoneNumber,
        organizationId,
      });
      if (!result.success) { throw result; }

      toast({
        title: "Success",
        description: "User created successfully",
      });

      reset();
      setPhoneInput("");
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to create user",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setPhoneInput("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Enter user name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={phoneInput}
                  onChange={handlePhoneInputChange}
                  onBlur={field.onBlur}
                  placeholder="Enter phone number"
                  disabled={isSubmitting}
                />
              )}
            />
            {errors?.phoneNumber && (
              <p className="text-red-500 text-sm">{errors.phoneNumber.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

